/** Ticketing event definitions — catalog 1.8.1 (Phase 3B review remediation). */
import {
  asEventRecord,
  defineEvent,
  requireString,
  type EventDefinition,
  type EventSchema,
} from './registry';

interface TicketingEventBase extends Record<string, unknown> {
  orgId: string;
  showtimeId: string;
}

export interface ShowtimeScheduledPayload extends TicketingEventBase {
  activityId: string;
  startsAt: string;
}

export interface ShowtimeCancelledPayload extends TicketingEventBase {}

export interface SeatHeldPayload extends TicketingEventBase {
  seatId: string;
  claimId: string;
  expiresAt: string;
}

export interface SeatReleasedPayload extends TicketingEventBase {
  seatId: string;
}

export interface SeatSoldPayload extends TicketingEventBase {
  seatId: string;
  orderId: string;
}

export interface SeatStateChangedPayload extends TicketingEventBase {
  seatId: string;
  state: 'available' | 'sold' | 'locked' | 'unavailable' | 'maintenance';
}

export interface CapacityHeldPayload extends TicketingEventBase {
  claimId: string;
  units: number;
  expiresAt: string;
}

export interface CapacityReleasedPayload extends TicketingEventBase {
  claimId: string;
  units: number;
}

function ticketingEventSchema<TPayload extends TicketingEventBase>(
  type: string,
  fields: readonly string[],
  integerFields: readonly string[] = [],
): EventSchema<TPayload> {
  return {
    parse(data) {
      const record = asEventRecord(data, type);
      const parsed: Record<string, unknown> = {};

      for (const field of fields) {
        const value = record[field];
        if (integerFields.includes(field)) {
          if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 1) {
            throw new Error(`${type}: field "${field}" must be a positive integer`);
          }
          parsed[field] = value;
        } else {
          parsed[field] = requireString(value, field, type);
        }
      }

      if (type === 'ticketing:seat-state-changed' &&
        !['available', 'sold', 'locked', 'unavailable', 'maintenance'].includes(String(parsed.state))) {
        throw new Error(`${type}: field "state" must be a valid seat state`);
      }

      return parsed as TPayload;
    },
  };
}

function defineTicketingEvent<TPayload extends TicketingEventBase>(
  type: string,
  description: string,
  fields: readonly string[],
  integerFields: readonly string[] = [],
  version = 1,
  since = '1.8.0',
): EventDefinition<TPayload> {
  return defineEvent<TPayload>({
    type,
    version,
    schema: ticketingEventSchema<TPayload>(type, fields, integerFields),
    auditable: true,
    description,
    since,
  });
}

export const TICKETING_SHOWTIME_SCHEDULED_EVENT = defineTicketingEvent<ShowtimeScheduledPayload>(
  'ticketing:showtime-scheduled',
  'A showtime was scheduled for an organization.',
  ['orgId', 'showtimeId', 'activityId', 'startsAt'],
);

export const TICKETING_SHOWTIME_CANCELLED_EVENT = defineTicketingEvent<ShowtimeCancelledPayload>(
  'ticketing:showtime-cancelled',
  'A scheduled showtime was cancelled.',
  ['orgId', 'showtimeId'],
);

export const TICKETING_SEAT_HELD_EVENT = defineTicketingEvent<SeatHeldPayload>(
  'ticketing:seat-held',
  'A seat was claimed temporarily for a showtime.',
  ['orgId', 'showtimeId', 'seatId', 'claimId', 'expiresAt'],
  [],
  2,
  '1.8.1',
);

export const TICKETING_SEAT_RELEASED_EVENT = defineTicketingEvent<SeatReleasedPayload>(
  'ticketing:seat-released',
  'A seat claim was released or expired.',
  ['orgId', 'showtimeId', 'seatId'],
);

export const TICKETING_SEAT_SOLD_EVENT = defineTicketingEvent<SeatSoldPayload>(
  'ticketing:seat-sold',
  'A claimed seat was sold through an order.',
  ['orgId', 'showtimeId', 'seatId', 'orderId'],
);

export const TICKETING_SEAT_STATE_CHANGED_EVENT = defineTicketingEvent<SeatStateChangedPayload>(
  'ticketing:seat-state-changed',
  'A showtime seat state changed, including its transition to available.',
  ['orgId', 'showtimeId', 'seatId', 'state'],
  [],
  2,
  '1.8.1',
);

export const TICKETING_CAPACITY_HELD_EVENT = defineTicketingEvent<CapacityHeldPayload>(
  'ticketing:capacity-held',
  'Capacity was claimed temporarily for a showtime.',
  ['orgId', 'showtimeId', 'claimId', 'units', 'expiresAt'],
  ['units'],
  2,
  '1.8.1',
);

export const TICKETING_CAPACITY_RELEASED_EVENT = defineTicketingEvent<CapacityReleasedPayload>(
  'ticketing:capacity-released',
  'A showtime capacity claim was released or expired.',
  ['orgId', 'showtimeId', 'claimId', 'units'],
  ['units'],
  2,
  '1.8.1',
);

export const TICKETING_EVENTS: Readonly<Record<string, EventDefinition>> = {
  [TICKETING_SHOWTIME_SCHEDULED_EVENT.type]: TICKETING_SHOWTIME_SCHEDULED_EVENT,
  [TICKETING_SHOWTIME_CANCELLED_EVENT.type]: TICKETING_SHOWTIME_CANCELLED_EVENT,
  [TICKETING_SEAT_HELD_EVENT.type]: TICKETING_SEAT_HELD_EVENT,
  [TICKETING_SEAT_RELEASED_EVENT.type]: TICKETING_SEAT_RELEASED_EVENT,
  [TICKETING_SEAT_SOLD_EVENT.type]: TICKETING_SEAT_SOLD_EVENT,
  [TICKETING_SEAT_STATE_CHANGED_EVENT.type]: TICKETING_SEAT_STATE_CHANGED_EVENT,
  [TICKETING_CAPACITY_HELD_EVENT.type]: TICKETING_CAPACITY_HELD_EVENT,
  [TICKETING_CAPACITY_RELEASED_EVENT.type]: TICKETING_CAPACITY_RELEASED_EVENT,
};
